/* eslint-disable @typescript-eslint/no-explicit-any */
import { FieldPolicy, FieldReadFunction } from '@apollo/client/cache';
import { relayStylePagination } from '@apollo/client/utilities';

// relative imports
import {
  getDate,
  handleTypePolicyDates,
  isEpochStart,
} from '../../../components/shared/utilities/dateUtilities';
import {
  QboIntegrationFieldPolicy,
  QueryFieldPolicy,
  StrictTypedTypePolicies,
  XeroIntegrationFieldPolicy,
} from './apollo-helpers';
import { accountingTableStateVar, formStateVar } from './reactiveVars';

type AccountingIntegrationUnionFieldPolicy = {
  fields: XeroIntegrationFieldPolicy | QboIntegrationFieldPolicy;
};

type QueryFieldPolicyWithLocalOnlyFields = QueryFieldPolicy & {
  currentAccountId?: FieldPolicy<any> | FieldReadFunction<any>;
  accountingTableState?: FieldPolicy<any> | FieldReadFunction<any>;
  formState?: FieldPolicy<any> | FieldReadFunction<any>;
};

interface ExtendedStrictTypePolicies extends StrictTypedTypePolicies {
  [key: string]: any;
  Query: {
    fields: QueryFieldPolicyWithLocalOnlyFields;
  };
  AccountingIntegration: AccountingIntegrationUnionFieldPolicy;
}

/**
 * Type Policies - Define any client-side cache logic
 * for client or server schema types
 */
const typePolicies: ExtendedStrictTypePolicies = {
  Query: {
    fields: {
      accountingTableState: {
        read(_, { variables }) {
          return (
            accountingTableStateVar()?.[variables?.accountId] ||
            accountingTableStateVar() ||
            {}
          );
        },
      },
      formState: {
        read() {
          return formStateVar();
        },
      },
      getAccounts: {
        merge(existing, incoming) {
          return incoming;
        },
      },
      wallets: {
        merge(existing, incoming) {
          return incoming;
        },
      },
      getWalletsUpdating: {
        merge(existing, incoming) {
          return incoming;
        },
      },
      getIncomeBreakdown: {
        merge(existing, incoming) {
          return incoming;
        },
      },
      getGainLossPerYear: {
        merge(existing, incoming) {
          return incoming;
        },
      },
      getPaginatedTxesForTax: relayStylePagination(),
      getLogsByFilter: relayStylePagination(),
      getExchangesUpdating: {
        merge(existing, incoming) {
          return incoming;
        },
      },
      getReportsByFilter: {
        keyArgs: [
          'accountId',
          'filters',
          ['id', 'value', 'range'],
          'clientAccountName',
          'sortBy',
          ['id', 'desc'],
        ],
        merge: true,
      },
      getPreparerLinksByFilter: {
        keyArgs: [
          'accountId',
          'filters',
          ['id', 'arrayValue', 'value'],
          'clientAccountName',
          'sortBy',
          ['id', 'desc'],
          'tirTaxYear',
        ],
        merge: true,
      },
      getCurrencies: {
        keyArgs: ['accountId', 'filter'],
        merge(existing, incoming) {
          return incoming;
        },
      },
      walletsSummary: {
        merge(existing, incoming) {
          return incoming;
        },
      },
      eSourceSummary: {
        merge(existing, incoming) {
          return incoming;
        },
      },
    },
  },
  AccountingIntegration: {
    fields: {
      lastSyncedWallet: {
        read(existing: string) {
          if (isEpochStart(existing)) {
            return null;
          }
          return handleTypePolicyDates(existing, 'MMM d, yyyy, h:mm a');
        },
      },
      lastSyncedExchange: {
        read(existing: string) {
          if (isEpochStart(existing)) {
            return null;
          }
          return handleTypePolicyDates(existing, 'MMM d, yyyy, h:mm a');
        },
      },
    },
  },
  Account: {
    merge: true,
    fields: {
      // taxBilling is non-normalized, so merge partial writes, normalize arrays on read
      taxBilling: {
        merge(existing, incoming, { mergeObjects }) {
          return mergeObjects(existing, incoming);
        },
        read(existing: Record<string, unknown> | undefined) {
          if (existing == null) return existing;

          return {
            ...existing,
            tierPrice: Array.isArray(existing.tierPrice)
              ? existing.tierPrice
              : [],
            overagePrice: Array.isArray(existing.overagePrice)
              ? existing.overagePrice
              : [],
          };
        },
      },
    },
  },
  TaxPayerDetails: {
    merge: true,
  },
  TaxPreparerDetails: {
    merge: true,
  },
  TaxPreparerBillingDetails: {
    merge: true,
  },
  Plan: {
    merge: true,
  },
  AccountApplicationType: {
    merge: true,
  },
  AccountAdminData: {
    merge: true,
  },
  TxTableSettings: {
    merge: true,
  },
  AccountApplicationTypeFields: {
    merge(existing, incoming) {
      return incoming;
    },
  },
  Balance: {
    fields: {
      balances: {
        merge(existing, incoming) {
          return incoming;
        },
      },
    },
  },
  BalanceAudit: {
    fields: {
      timestamp: {
        read(existing) {
          return handleTypePolicyDates(existing, 'MM/d/yy');
        },
      },
    },
  },
  BalanceInstance: {
    merge: true,
    keyFields: ['currency', 'amount'],
  },
  Order: {
    merge: true,
    fields: {
      Timestamp: {
        read(existing) {
          return handleTypePolicyDates(existing, 'MMM d, yyyy, h:mm a');
        },
      },
    },
  },
  Transaction: {
    merge: true,
    fields: {
      Timestamp: {
        read(existing) {
          return handleTypePolicyDates(existing, 'MMM d, yyyy, h:mm a');
        },
      },
    },
  },
  Report: {
    fields: {
      requested: {
        read(existing) {
          return handleTypePolicyDates(existing, 'M/d/yyyy, h:mm a');
        },
      },
      parameters: {
        merge(existing, incoming) {
          return incoming;
        },
      },
    },
  },
  LiquidityPoolMovement: {
    fields: {
      timestamp: {
        read(existing) {
          return handleTypePolicyDates(existing, 'MMM d, yyyy, h:mm a');
        },
      },
    },
  },
  LiquidityPoolTransactions: {
    fields: {
      fromDate: {
        read(existing) {
          return getDate(existing).toString();
        },
      },
      toDate: {
        read(existing) {
          return getDate(existing).toString();
        },
      },
    },
  },
  TaxTx: {
    merge: true,
    fields: {
      timestamp: {
        read(existing) {
          return handleTypePolicyDates(existing, 'M/d/yyyy, h:mm:ss a');
        },
      },
      reconciliations: {
        merge(existing, incoming) {
          return incoming;
        },
      },
    },
  },
  TaxTxCustomData: {
    merge: true,
  },
  MatchRef: {
    merge: true,
  },
  CostBasisAssignmentsLots: {
    merge: true,
  },
  SpecIdTransaction: {
    merge: true,
  },
  Memo: {
    merge: true,
  },
  TaxPreparerLink: {
    // @ts-ignore - Custom keyFields function for composite keys
    keyFields(object: any, context: any) {
      const id = context.readField('id', object);
      const carfData: {
        taxYear: number;
        jurisdiction: string;
        reportableEvents: number;
        qualifyingTags: string[];
      } = context.readField('carfData', object);

      if (carfData?.taxYear) {
        return `TaxPreparerLink:${id}:${carfData.taxYear}`;
      }

      return `TaxPreparerLink:${id}`;
    },
    merge: true,
    fields: {
      createdAt: {
        read(existing) {
          return handleTypePolicyDates(existing, 'M/d/yyyy');
        },
      },
      codeLastSent: {
        read(existing) {
          if (existing) return handleTypePolicyDates(existing, 'M/d/yyyy');
          return null;
        },
      },
      // Same as Account.taxBilling: merge partial writes, normalize arrays on read
      taxBilling: {
        merge(existing, incoming, { mergeObjects }) {
          return mergeObjects(existing, incoming);
        },
        read(existing: Record<string, unknown> | undefined) {
          if (existing == null) return existing;
          return {
            ...existing,
            tierPrice: Array.isArray(existing.tierPrice)
              ? existing.tierPrice
              : [],
            overagePrice: Array.isArray(existing.overagePrice)
              ? existing.overagePrice
              : [],
          };
        },
      },
    },
  },
  PrepTypeFields: {
    merge: true,
  },
  TaxPreparerCreditDetails: {
    merge: true,
  },
  TmpUser: {
    keyFields: ['email'],
    fields: {
      validTill: {
        read(existing) {
          return getDate(existing).toString();
        },
      },
    },
  },
  TaxReconciliation: {
    merge: true,
    fields: {
      timestamp: {
        read(existing) {
          return handleTypePolicyDates(existing, 'M/d/yyyy, h:mm a');
        },
      },
    },
  },
  // ExchangeCredentialType uses a semantic 'id' is not globally unique
  ExchangeCredentialType: {
    keyFields: false,
  },
  ESource: {
    fields: {
      orders: {
        merge(existing, incoming = []) {
          return incoming;
        },
      },
    },
  },
  Wallet: {
    fields: {
      // custom merge for addresses to accommodate offset and limit args + id keyarg
      addresses: {
        keyArgs: ['id'],
        merge(
          existing = [],
          incoming = [],
          { args = { offset: 0, limit: 0 } },
        ) {
          const merged = existing ? [...existing] : [];
          const incomingOffset = args?.offset || 0;
          const incomingLimit = args?.limit || incoming.length;

          // Ensure array is large enough to accommodate offset + limit
          const targetLength = incomingOffset + incomingLimit;
          while (merged.length < targetLength) {
            merged.push(null);
          }

          // Insert incoming items at their offset
          for (let i = 0; i < incoming.length; i += 1) {
            const item = incoming[i];
            const index = incomingOffset + i;

            // Check if item with same ID already exists
            const existingIndex = merged.findIndex(
              (existingAddress) => existingAddress?.id === item.id,
            );

            if (existingIndex >= 0) {
              // Update existing item
              merged[existingIndex] = item;
              // Remove duplicate position if different from current index
              if (existingIndex !== index) {
                merged[index] = null;
              }
            } else {
              // Insert at offset position
              merged[index] = item;
            }
          }

          // Remove any null entries
          return merged.filter(Boolean);
        },
      },
      lastUpdated: {
        read(existing) {
          return handleTypePolicyDates(existing, 'M/d/yyyy, h:mm a');
        },
      },
      transactions: {
        merge(existing, incoming = []) {
          return incoming;
        },
      },
    },
  },
  UsagePlan: {
    merge: true,
  },
  UserProfile: {
    keyFields: ['email'],
    merge: true,
  },
  UserPermission: {
    keyFields: ['accountId', 'userId'],
  },
  AccountStatistics: {
    merge: true,
  },
  TaxUpdater: {
    merge: true,
  },
  AddedUser: {
    keyFields: ['email'],
  },
  TaxAddons: {
    merge: true,
  },
  LiquidityPool: {
    keyFields: ['address', 'poolAddress'],
    merge: true,
  },
  ESourceListItem: {
    keyFields: ['name'],
  },
  Chain: {
    keyFields: ['symbol'],
  },
  SummaryReport: {
    fields: {
      assets: {
        merge(existing, incoming) {
          return incoming;
        },
      },
    },
  },
  Summary: {
    fields: {
      assets: {
        merge(existing, incoming) {
          return incoming;
        },
      },
    },
  },
  TxTableRow: {
    merge: true,
    fields: {
      txDate: {
        read(existing) {
          return handleTypePolicyDates(existing, 'MMM d, yyyy, h:mm a');
        },
      },
      memos: {
        merge(existing, incoming) {
          return incoming || existing;
        },
      },
    },
  },
  IncomeBreakdown: {
    merge: true,
  },
  YearlyGainLoss: {
    merge: true,
  },
  CustomInoutData: {
    merge: true,
  },
  TaxBillingDetails: {
    keyFields: ['accountId'],
    merge: true,
  },
  PaymentHistory: {
    merge: true,
  },
  FeedConnection: {
    fields: {
      edges: {
        merge(existing = [], incoming, { readField, variables = {} }) {
          const { feedType } = variables;

          // skip merge for certain feed types as there is no persistence of multiple pages
          if (feedType === 'TAXTX' || feedType === 'LOG') {
            return [...incoming];
          }
          // else use unique set logic
          const edgesRef = existing?.reduce((edgeRef: any, item: any) => {
            edgeRef[readField('cursor', item) as keyof typeof edgeRef] = item;
            return edgeRef;
          }, {});

          incoming.forEach((item: any) => {
            const key = readField('cursor', item);
            if (key) edgesRef[key as keyof typeof edgesRef] = item;
          });

          return Object.values(edgesRef);
        },
      },
    },
  },
  Log: {
    fields: {
      timestamp: {
        read(existing) {
          return handleTypePolicyDates(existing, 'MM/dd/yyyy');
        },
      },
    },
  },
  UserControl: {
    keyFields: false,
    merge: true,
  },
  UserChanges: {
    keyFields: false,
    merge: true,
  },
  UserMetadata: {
    keyFields: false,
    merge: true,
  },
  TaxTxContext: {
    keyFields: false,
    merge: true,
  },
  GcpFile: {
    fields: {
      createdAt: {
        read(existing) {
          return handleTypePolicyDates(existing, 'M/d/yyyy, h:mm a');
        },
      },
      updatedAt: {
        read(existing) {
          return handleTypePolicyDates(existing, 'M/d/yyyy, h:mm a');
        },
      },
    },
  },
};

export default typePolicies;
